import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/label';
import { PageHeader, Loading, ErrorState } from '@/components/ui/states';
import { useToast } from '@/components/ui/toast';
import { useSettings, useUpdateSettings } from '@/api/hooks';
import { errorMessage } from '@/api/client';

export function SettingsPage() {
  const settings = useSettings();
  const update = useUpdateSettings();
  const { toast } = useToast();

  const [adminChatIds, setAdminChatIds] = useState('');
  const [threshold, setThreshold] = useState('0');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [botToken, setBotToken] = useState('');
  const [webAuthSecret, setWebAuthSecret] = useState('');
  const [publicPriceMarkupPercent, setPublicPriceMarkupPercent] = useState('50');
  const [binanceApiKey, setBinanceApiKey] = useState('');
  const [binanceApiSecret, setBinanceApiSecret] = useState('');
  const [usdtTrc20Address, setUsdtTrc20Address] = useState('');
  const [usdtBep20Address, setUsdtBep20Address] = useState('');
  const [binancePayId, setBinancePayId] = useState('');
  const [jazzcashNumber, setJazzcashNumber] = useState('+923236366806');
  const [jazzcashAccountName, setJazzcashAccountName] = useState('Junaid Mureed');
  const [jazzcashPkrPerUsdt, setJazzcashPkrPerUsdt] = useState('300');
  const [requiredGroupChatId, setRequiredGroupChatId] = useState('');
  const [requiredGroupInviteLink, setRequiredGroupInviteLink] = useState('');
  const [requiredChannelChatId, setRequiredChannelChatId] = useState('');
  const [requiredChannelInviteLink, setRequiredChannelInviteLink] = useState('');

  useEffect(() => {
    if (settings.data) {
      setAdminChatIds(settings.data.adminChatIds.join(', '));
      setThreshold(String(settings.data.defaultWalletAlertThreshold));
      setWebhookUrl(settings.data.webhookUrl);
      setPublicPriceMarkupPercent(String(settings.data.publicPriceMarkupPercent ?? 50));
      setUsdtTrc20Address(settings.data.usdtTrc20Address ?? '');
      setUsdtBep20Address(settings.data.usdtBep20Address ?? '');
      setBinancePayId(settings.data.binancePayId ?? '');
      setJazzcashNumber(settings.data.jazzcashNumber ?? '+923236366806');
      setJazzcashAccountName(settings.data.jazzcashAccountName ?? 'Junaid Mureed');
      setJazzcashPkrPerUsdt(String(settings.data.jazzcashPkrPerUsdt ?? 300));
      setRequiredGroupChatId(settings.data.requiredGroupChatId ?? '');
      setRequiredGroupInviteLink(settings.data.requiredGroupInviteLink ?? '');
      setRequiredChannelChatId(settings.data.requiredChannelChatId ?? '');
      setRequiredChannelInviteLink(settings.data.requiredChannelInviteLink ?? '');
    }
  }, [settings.data]);

  if (settings.isLoading) return <Loading />;
  if (settings.isError) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;

  function save() {
    const ids = adminChatIds
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n));
    update.mutate(
      {
        adminChatIds: ids,
        defaultWalletAlertThreshold: Number(threshold) || 0,
        webhookUrl: webhookUrl || undefined,
        botToken: botToken || undefined,
        webAuthSecret: webAuthSecret || undefined,
        publicPriceMarkupPercent: Math.max(0, Math.min(1000, Number(publicPriceMarkupPercent) || 0)),
        binanceApiKey: binanceApiKey || undefined,
        binanceApiSecret: binanceApiSecret || undefined,
        usdtTrc20Address,
        usdtBep20Address,
        binancePayId,
        jazzcashNumber,
        jazzcashAccountName,
        jazzcashPkrPerUsdt: Number(jazzcashPkrPerUsdt) || 300,
        requiredGroupChatId,
        requiredGroupInviteLink,
        requiredChannelChatId,
        requiredChannelInviteLink,
      },
      {
        onSuccess: (result) => {
          setBotToken(''); setWebAuthSecret(''); setBinanceApiKey(''); setBinanceApiSecret('');
          toast({ title: 'Settings saved', description: result.restartRequired ? 'Restart the API to activate bot changes.' : undefined, variant: 'success' });
        },
        onError: (e) => toast({ title: 'Save failed', description: errorMessage(e), variant: 'error' }),
      },
    );
  }

  return (
    <div className="max-w-2xl">
      <PageHeader title="Settings" description="Secure runtime configuration for Telegram and Binance payments." />

      <Card>
        <CardHeader>
          <CardTitle>General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field
            label="Admin Telegram chat IDs"
            hint={`Comma-separated numeric IDs. From env: ${settings.data?.env?.adminChatIdsFromEnv.join(', ') || '(none)'}`}
          >
            <Input value={adminChatIds} onChange={(e) => setAdminChatIds(e.target.value)} placeholder="123456789, 987654321" />
          </Field>

          <Field label="Default wallet alert threshold (USDT)" hint="Used when a supplier has no threshold of its own.">
            <Input type="number" step="0.01" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </Field>

          <Field label="Telegram bot token" hint={`${settings.data?.botTokenConfigured ? 'Token stored.' : 'Not configured.'} ${settings.data?.botStatus.connected ? `Connected as @${settings.data.botStatus.username}` : settings.data?.botStatus.error ?? 'Disconnected'}`}>
            <Input type="password" autoComplete="new-password" value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder="Leave blank to keep current token" />
          </Field>

          <Field
            label="Webhook URL"
            hint={`Public HTTPS base URL for the Telegram webhook. From env: ${settings.data?.env?.webhookUrlFromEnv || '(none)'}`}
          >
            <Input value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://yourdomain.com" />
          </Field>

          <div className="space-y-4 border-t pt-4">
            <div>
              <h3 className="font-medium">Public web store</h3>
              <p className="text-xs text-muted-foreground">
                These values take effect immediately. Changing the authentication secret signs out all existing web customers.
              </p>
            </div>
            <Field
              label="Web commission (%)"
              hint={`Added to Telegram prices on the public store. Environment fallback: ${settings.data?.env?.publicPriceMarkupPercent ?? 50}%`}
            >
              <Input type="number" min="0" max="1000" step="0.01" value={publicPriceMarkupPercent} onChange={(e) => setPublicPriceMarkupPercent(e.target.value)} />
            </Field>
            <Field
              label="Web authentication secret"
              hint={settings.data?.webAuthSecretConfigured ? 'Secret configured. Leave blank to keep it.' : 'Not configured. Enter at least 32 characters.'}
            >
              <Input type="password" minLength={32} autoComplete="new-password" value={webAuthSecret} onChange={(e) => setWebAuthSecret(e.target.value)} placeholder="Leave blank to keep the current secret" />
            </Field>
          </div>

          <div className="space-y-4 border-t pt-4">
            <div>
              <h3 className="font-medium">Payment methods</h3>
              <p className="text-xs text-muted-foreground">
                Buyers see whichever of these are filled in. USDT addresses are auto-verified against your Binance
                deposit history (TxID paste); Binance Pay ID has no auto-verify API available, so it always queues in
                the Wallet page for you to manually approve.
              </p>
            </div>
            <Field label="Binance API key" hint={settings.data?.binanceConfigured ? 'Credentials stored encrypted; blanks keep them.' : 'Needed for USDT auto-verify.'}><Input type="password" autoComplete="new-password" value={binanceApiKey} onChange={(e) => setBinanceApiKey(e.target.value)} /></Field>
            <Field label="Binance API secret" hint="Use a read-only key with deposit-history access. Never enable withdrawals."><Input type="password" autoComplete="new-password" value={binanceApiSecret} onChange={(e) => setBinanceApiSecret(e.target.value)} /></Field>
            <Field label="USDT (TRC20) address" hint="Leave blank to hide this option from buyers"><Input value={usdtTrc20Address} onChange={(e) => setUsdtTrc20Address(e.target.value)} placeholder="T…" /></Field>
            <Field label="USDT (BEP20) address" hint="Leave blank to hide this option from buyers"><Input value={usdtBep20Address} onChange={(e) => setUsdtBep20Address(e.target.value)} placeholder="0x…" /></Field>
            <Field label="Binance Pay ID" hint="Your Binance UID. Leave blank to hide this option. Always manual review."><Input value={binancePayId} onChange={(e) => setBinancePayId(e.target.value)} placeholder="1060734979" /></Field>
            <Field label="JazzCash number" hint="Leave blank to hide JazzCash from checkout."><Input value={jazzcashNumber} onChange={(e) => setJazzcashNumber(e.target.value)} placeholder="+923236366806" /></Field>
            <Field label="JazzCash account holder"><Input value={jazzcashAccountName} onChange={(e) => setJazzcashAccountName(e.target.value)} placeholder="Junaid Mureed" /></Field>
            <Field label="JazzCash exchange rate (PKR per USDT)" hint="The PKR invoice total is calculated from the locked USDT order total."><Input type="number" min="0.01" step="0.01" value={jazzcashPkrPerUsdt} onChange={(e) => setJazzcashPkrPerUsdt(e.target.value)} /></Field>
          </div>

          <div className="space-y-4 border-t pt-4">
            <div>
              <h3 className="font-medium">Force-join gate</h3>
              <p className="text-xs text-muted-foreground">
                Require users to join a group and/or channel before they can use the bot. Leave both chat IDs blank to
                disable this — it takes effect immediately, no restart needed.
              </p>
            </div>

            <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1.5">
              <p className="font-medium text-foreground">How to set this up:</p>
              <p>1. Create a Telegram group and/or channel (or use existing ones).</p>
              <p>
                2. Add your bot to it as an <b>admin</b> (Group/Channel info → Administrators → Add Admin) — required for
                the bot to check who has joined.
              </p>
              <p>
                3. If it's <b>public</b>, enter its @username below (e.g. <code>@myproductgroup</code>) — the invite
                link is derived automatically.
              </p>
              <p>
                4. If it's <b>private</b>, get the numeric chat ID instead: forward any message from that group/channel
                to <code>@userinfobot</code> (or <code>@getidsbot</code>), it replies with an ID like{' '}
                <code>-1001234567890</code> — paste that as the chat ID, and paste the group/channel's invite link
                (Info → Invite Link) into the link field below so users have a way to join it.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Group chat ID" hint="@username or -100… numeric ID">
                <Input value={requiredGroupChatId} onChange={(e) => setRequiredGroupChatId(e.target.value)} placeholder="@mygroup" />
              </Field>
              <Field label="Group invite link" hint="Only needed for a private group">
                <Input value={requiredGroupInviteLink} onChange={(e) => setRequiredGroupInviteLink(e.target.value)} placeholder="https://t.me/+…" />
              </Field>
              <Field label="Channel chat ID" hint="@username or -100… numeric ID">
                <Input value={requiredChannelChatId} onChange={(e) => setRequiredChannelChatId(e.target.value)} placeholder="@mychannel" />
              </Field>
              <Field label="Channel invite link" hint="Only needed for a private channel">
                <Input value={requiredChannelInviteLink} onChange={(e) => setRequiredChannelInviteLink(e.target.value)} placeholder="https://t.me/+…" />
              </Field>
            </div>
          </div>

          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            Scheduler: <b>{settings.data?.env?.schedulerEnabled ? 'enabled' : 'disabled'}</b>. Changing the webhook URL here
            does not re-register with Telegram — run <code>pnpm --filter @aggregator/api set-webhook</code> after deploying.
          </div>

          <div className="flex justify-end">
            <Button loading={update.isPending} onClick={save}>
              Save settings
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
